import { calculateSlots, type SlotInput } from '../hours/calculateSlots'
import type { NightConfig } from '../hours/nightHours'
import { addBonuses } from './addBonuses'
import { minutesToDecimalHours } from './convertDecimal'

/**
 * Entrée complète pour le calcul des totaux d'une mission.
 *
 * - `slots` : liste des créneaux horaires (1 à N, les `null` / `undefined`
 *   sont tolérés et ignorés par `calculateSlots`).
 * - `washing` : booléen — forfait lavage appliqué si `true`.
 * - `travel` : string flexible ("1h30", "1,5", "0:45"…) parsée par
 *   `parseFlexibleHours` ; valeurs invalides → 0 minute.
 * - `nightConfig` : configuration optionnelle de la plage horaire nuit.
 *   Par défaut 22:00 → 06:00 (cf. `DEFAULT_NIGHT_CONFIG`).
 *
 * `washing` et `travel` sont intentionnellement requis, pas optionnels :
 * un service qui oublie de les passer aura une erreur de compilation
 * plutôt qu'un calcul silencieusement faux.
 */
export type MissionInput = {
  readonly slots: readonly SlotInput[]
  readonly washing: boolean
  readonly travel: string
  readonly nightConfig?: NightConfig
}

/**
 * Résultat complet du calcul d'une mission.
 *
 * **En minutes (valeurs brutes, pour stockage et calculs internes)** :
 * - `totalMinutes` : durée totale y compris bonus.
 * - `dayMinutes`, `nightMinutes` : ventilation jour/nuit des *slots uniquement*
 *   (les bonus ne sont pas ventilés, cf. `addBonuses`).
 * - `washingMinutes`, `travelMinutes`, `bonusMinutes` : détail des bonus.
 *
 * **En heures décimales (pour facturation et affichage)** :
 * - `totalHours`, `dayHours`, `nightHours` : chaque valeur est arrondie
 *   **indépendamment** au quart d'heure via `minutesToDecimalHours`.
 *
 * ⚠️ **L'invariant `totalHours = dayHours + nightHours + bonusHours` n'est
 * PAS garanti.** L'arrondi au quart d'heure n'est pas distributif : la
 * facturation arrondit chaque ligne séparément, pas le total.
 *
 * Exemple : `dayMinutes=100` → `dayHours=1.75`, `nightMinutes=100` →
 * `nightHours=1.75`, mais `totalMinutes=200` → `totalHours=3.25` (pas 3.5).
 *
 * Les **invariants garantis** sont exprimés en minutes brutes :
 * - `totalMinutes = dayMinutes + nightMinutes + bonusMinutes`
 * - `bonusMinutes = washingMinutes + travelMinutes`
 *
 * **Pas de `bonusHours` exposé** : l'arrondi séparé créerait une fausse
 * attente d'additivité. Le consommateur qui veut afficher un bonus en
 * heures décimales peut appeler `minutesToDecimalHours(bonusMinutes)`
 * explicitement, en connaissance de cause.
 */
export type MissionTotals = {
  readonly totalMinutes: number
  readonly dayMinutes: number
  readonly nightMinutes: number
  readonly washingMinutes: number
  readonly travelMinutes: number
  readonly bonusMinutes: number
  readonly totalHours: number
  readonly dayHours: number
  readonly nightHours: number
}

/**
 * Orchestre le calcul complet des totaux d'une mission.
 *
 * Pipeline, dans l'ordre :
 * 1. `calculateSlots(slots, nightConfig)` → `{ totalMinutes, dayMinutes,
 *    nightMinutes }` agrégés sur tous les créneaux.
 * 2. `addBonuses(slotTotals, { washing, travel })` → ajoute
 *    `washingMinutes`, `travelMinutes`, `bonusMinutes`, et incrémente
 *    `totalMinutes` de `bonusMinutes`.
 * 3. `minutesToDecimalHours()` appliqué **indépendamment** sur
 *    `totalMinutes`, `dayMinutes` et `nightMinutes`.
 *
 * **Fonction pure** : aucune logique de calcul, aucune constante, aucun
 * `if` métier ici. Uniquement du câblage et du mapping. Si cet
 * orchestrateur se met à contenir une formule, c'est qu'un module aval
 * est incomplet.
 *
 * **Gestion d'erreur transparente** : `calculateSlots` peut propager
 * `InvalidNightConfigError` si `nightConfig` est hors bornes (0..23) ou
 * ne traverse pas minuit. Cette erreur n'est **pas** capturée ici : elle
 * remonte telle quelle au service appelant, qui décidera de la traduire
 * en 400 HTTP, log, etc. Un problème de configuration doit échouer
 * bruyamment.
 *
 * Ne mute ni `input.slots` ni `input.nightConfig`.
 *
 * @example
 *   computeMissionTotals({
 *     slots: [{ start: '08:00', end: '17:00' }],
 *     washing: true,
 *     travel: '1h30',
 *   })
 *   // {
 *   //   totalMinutes: 660, dayMinutes: 540, nightMinutes: 0,
 *   //   washingMinutes: 30, travelMinutes: 90, bonusMinutes: 120,
 *   //   totalHours: 11, dayHours: 9, nightHours: 0,
 *   // }
 *
 * @param input - Slots + bonus + config nuit optionnelle.
 * @returns Totaux en minutes brutes et en heures décimales.
 * @throws {InvalidNightConfigError} Si `input.nightConfig` est malformée.
 */
export function computeMissionTotals(input: MissionInput): MissionTotals {
  const slotTotals = calculateSlots(input.slots, input.nightConfig)
  const withBonuses = addBonuses(slotTotals, {
    washing: input.washing,
    travel: input.travel,
  })

  return {
    totalMinutes: withBonuses.totalMinutes,
    dayMinutes: withBonuses.dayMinutes,
    nightMinutes: withBonuses.nightMinutes,
    washingMinutes: withBonuses.washingMinutes,
    travelMinutes: withBonuses.travelMinutes,
    bonusMinutes: withBonuses.bonusMinutes,
    totalHours: minutesToDecimalHours(withBonuses.totalMinutes),
    dayHours: minutesToDecimalHours(withBonuses.dayMinutes),
    nightHours: minutesToDecimalHours(withBonuses.nightMinutes),
  }
}
